import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, Search } from "lucide-react";

export default function ImageLookup() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const focusHash = params.get("q");

  const fileRef = useRef(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [searching, setSearching] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    api
      .get("/images")
      .then((r) => setClusters(r.data))
      .finally(() => setLoading(false));
  }, []);

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadPreview(URL.createObjectURL(f));
    setUploadResult(null);
  };

  const runUploadSearch = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      toast.error("Select an image first");
      return;
    }
    setSearching(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/lookup/image/upload", fd);
      setUploadResult(data);
      toast.success(`Found ${data.match_count} near-duplicate(s)`);
    } catch (err) {
      toast.error("Search failed. Try a different image.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="image-lookup-page">
      <header>
        <div className="text-xs font-mono uppercase tracking-wider text-blue-700">
          Image intelligence
        </div>
        <h1 className="font-display font-bold tracking-tight text-3xl">
          Duplicate image detection
        </h1>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          Upload an image to search for near-duplicates across all
          collected advertisements using a 64-bit perceptual hash.
          Advertisements grouped below share hashes exactly.
        </p>
      </header>

      <section className="border border-slate-200 bg-white rounded-md p-5 max-w-3xl" data-testid="image-upload-section">
        <h2 className="font-display font-bold text-lg tracking-tight mb-3">
          Reverse image search
        </h2>
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-40 h-40 border-2 border-dashed border-slate-200 rounded-md grid place-items-center bg-slate-50 overflow-hidden">
            {uploadPreview ? (
              <img src={uploadPreview} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-xs text-slate-400 text-center px-2">
                No image selected
              </div>
            )}
          </div>
          <div className="flex-1 min-w-[220px] space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="text-sm"
              data-testid="image-upload-input"
            />
            <div className="flex gap-2">
              <Button
                onClick={runUploadSearch}
                disabled={searching}
                data-testid="image-upload-search"
                className="bg-blue-600 hover:bg-blue-700 gap-2"
              >
                <Search className="h-4 w-4" />
                {searching ? "Searching…" : "Search"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (fileRef.current) fileRef.current.value = "";
                  setUploadPreview("");
                  setUploadResult(null);
                }}
                data-testid="image-upload-reset"
              >
                Reset
              </Button>
            </div>
            {uploadResult && (
              <div className="text-xs text-slate-500 font-mono" data-testid="upload-result-summary">
                query hash <span className="text-slate-800">{uploadResult.query_hash}</span> · {uploadResult.match_count} match(es) within Hamming ≤ {uploadResult.threshold}
              </div>
            )}
          </div>
        </div>

        {uploadResult && uploadResult.matches?.length > 0 && (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="upload-matches">
            {uploadResult.matches.map((m) => (
              <Link
                to={`/ads/${m.id}`}
                key={m.id}
                data-testid={`upload-match-${m.id}`}
                className="border border-slate-200 rounded-md p-2 hover:bg-slate-50"
              >
                {m.image_url ? (
                  <img src={m.image_url} alt={m.title} className="w-full h-24 object-cover rounded-md" />
                ) : (
                  <div className="w-full h-24 bg-slate-100 rounded-md grid place-items-center text-xs text-slate-400">
                    No img
                  </div>
                )}
                <div className="text-xs mt-1 truncate font-medium">{m.title}</div>
                <div className="text-[11px] text-slate-500 font-mono flex justify-between">
                  <span>d={m.hamming_distance}</span>
                  <span>{Math.round(m.similarity * 100)}% sim.</span>
                </div>
              </Link>
            ))}
          </div>
        )}
        {uploadResult && uploadResult.match_count === 0 && (
          <div className="mt-4 text-sm text-slate-500">
            No near-duplicates found in the database.
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display font-bold text-lg tracking-tight mb-3">
          Existing clusters
        </h2>

        {loading && <div className="text-sm text-slate-500">Loading clusters…</div>}

        <div className="grid md:grid-cols-2 gap-4">
          {clusters.map((c) => (
            <div
              key={c.image_hash}
              data-testid={`cluster-${c.image_hash}`}
              className={`border bg-white rounded-md p-4 space-y-3 ${
                focusHash === c.image_hash
                  ? "border-blue-500 ring-1 ring-blue-200"
                  : "border-slate-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <ImageIcon className="h-4 w-4 text-slate-500" />
                  <span className="font-mono text-xs truncate" title={c.image_hash}>
                    {c.image_hash}
                  </span>
                </div>
                <span
                  className={`text-xs font-mono px-2 py-0.5 rounded-md ${
                    c.count > 1
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {c.count} ad{c.count === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(c.ads || []).slice(0, 3).map((a) => (
                  <Link
                    to={`/ads/${a.id}`}
                    key={a.id}
                    data-testid={`cluster-ad-${a.id}`}
                    className="block"
                  >
                    {a.image_url ? (
                      <img
                        src={a.image_url}
                        alt={a.title}
                        className="w-full h-20 object-cover rounded-md border border-slate-200"
                      />
                    ) : (
                      <div className="w-full h-20 bg-slate-100 rounded-md border border-slate-200 grid place-items-center text-xs text-slate-400">
                        No img
                      </div>
                    )}
                    <div className="text-[11px] mt-1 truncate text-slate-600">
                      {a.title}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
          {!loading && clusters.length === 0 && (
            <div className="text-sm text-slate-500">No image clusters found.</div>
          )}
        </div>
      </section>
    </div>
  );
}
